import React from "react";

import { Provider } from "react-redux";
import { browserHistory, Route, Router } from "react-router";
import { syncHistoryWithStore } from "react-router-redux";

import { ListingFsvPage } from "~/app/mylistings/fsv";
import { ListingDetailsPage } from "~/app/mylistings/listing-details";
import { ListingRulesPage } from "~/app/mylistings/rules";
import { ActivateListingsPage } from "~/app/your-space/activate-listings";
import { ArrivalGuidePage } from "~/app/your-space/arrival-guide";
import { SpaceRulesPage } from "~/app/your-space/rules";
import { SpaceDetailsPage } from "~/app/your-space/space-details";

import { IDV_TRIGGER_TYPES } from "../../app-payments/idv/idv.consts";
import { requireIDV } from "../../app-payments/idv/require-idv.hoc";
import ExampleFormContainer from "../components/reduxForms/forms/ExampleForm/ExampleFormContainer";
import { configStore } from "../store/configStore";

import AddASpaceAddressContainer from "./AddASpaceAddressContainer";
import AddASpaceCancellationContainer from "./AddASpaceCancellationContainer";
import AddASpaceOpHoursContainer from "./AddASpaceOpHoursContainer";
import AddASpaceTocContainer from "./AddASpaceTocContainer";
import App from "./App";
import { requireAuth } from "./AuthContainer";
import CalendarMainContainer from "./CalendarContainer";
import CleaningContainer from "./CleaningContainer";
import EditPriceContainer from "./EditPriceContainer";
import EventQuestionsContainer from "./EventQuestionsContainer";
import HostHubContainer from "./HostHubContainer";
import HostingConfirmationContainer from "./HostingConfirmationContainer";
import HostingEmbedsContainer from "./HostingEmbedsContainer";
import HostingPolicyContainer from "./HostingPolicyContainer";
import HostingProfileContainer from "./HostingProfileContainer";
import IdentityContainer from "./IdentityContainer";
import ListingsContainer from "./ListingsContainer";
import ListingTOCContainer from "./ListingTOCContainer";
import ManageAddonsContainer from "./ManageAddonsContainer";
import MyListingsContainer from "./MyListingsContainer";
import ParkingContainer from "./ParkingContainer";
import PaymentsContainer from "./PaymentsContainer";
import ProductionQuestionsContainer from "./ProductionQuestionsContainer";
import ReferContainer from "./ReferContainer";
import SetupContainer from "./SetupContainer";
import TransactableHOC from "./TransactableHOC";
import UploaderContainer from "./UploaderContainer";

const preloadedState = window.__PRELOADED_STATE__;

export const store = configStore(browserHistory, preloadedState);
const history = syncHistoryWithStore(browserHistory, store);

const RootMain = () => {
  history.listen(() => {
    if ("function" == typeof ((window || {}).analytics || {}).page) {
      window.analytics.page(window.location.pathname, {
        internalRef: window.location.search,
        internalReferrer: document.referrer,
      });
    }

    // Scrolls user to top on location change, can add paths to exclude if needed
    window.scrollTo(0, 0);
  });

  return (
    <Provider store={store}>
      <Router history={history}>
        <Route path="/" component={App}>
          <Route path="refer/host" component={requireAuth(ReferContainer)} />
          <Route path="example/example-form" component={ExampleFormContainer} />
          <Route
            path="calendar(/:spaceId)"
            component={requireAuth(CalendarMainContainer)}
          />
          <Route
            path="mylistings/:listingId/instant-book"
            component={requireAuth(ListingsContainer)}
          />
          <Route
            path="account/identity"
            component={requireAuth(IdentityContainer)}
          />
          <Route
            path="account/:flow(/:subFlow)(/:isEdit)"
            component={requireIDV(requireAuth(PaymentsContainer), {
              triggerType: IDV_TRIGGER_TYPES.bank_account,
              shouldSkip: !window.location.href.includes("account/bank"),
              cancelUrl: "/account/methods",
            })}
          />
          <Route path="account" component={requireAuth(PaymentsContainer)} />
          <Route
            path="host-hub(/:tab)"
            component={requireAuth(HostHubContainer)}
          />
          <Route
            path="hosting/embeds/:listingID"
            component={requireAuth(HostingEmbedsContainer)}
          />
          <Route
            path="your-space/:spaceId/profile"
            component={requireAuth(HostingProfileContainer)}
          />
          <Route
            path="your-space/:spaceId/policies"
            component={requireAuth(HostingPolicyContainer)}
          />
          <Route
            path="your-space/:spaceId/confirmation"
            component={requireAuth(HostingConfirmationContainer)}
          />
          <Route
            path="your-space/:spaceId/setup"
            component={requireAuth(SetupContainer)}
          />
          <Route
            path="your-space/:spaceId/parking"
            component={requireAuth(ParkingContainer)}
          />
          <Route
            path="your-space/:spaceId/activate-listings"
            component={requireAuth(ActivateListingsPage)}
          />
          <Route
            path="your-space/:spaceId/space-details"
            component={requireAuth(SpaceDetailsPage)}
          />
          <Route
            path="your-space/:spaceId/rules"
            component={requireAuth(SpaceRulesPage)}
          />
          <Route
            path="your-space/:spaceId/arrival-guide"
            component={requireAuth(ArrivalGuidePage)}
          />
          <Route
            path="hosting/add-ons(/:spaceID)"
            component={requireAuth(ManageAddonsContainer)}
          />
          <Route
            path="your-space/:spaceId/production-faqs/:page"
            component={requireAuth(ProductionQuestionsContainer)}
          />
          <Route
            path="your-space/:spaceId/event-faqs/:page"
            component={requireAuth(EventQuestionsContainer)}
          />
          <Route
            path="your-space/:spaceId/photos"
            component={requireAuth(UploaderContainer)}
          />
          <Route
            path="mylistings/:listingId/photos"
            component={requireAuth(UploaderContainer)}
          />
          <Route
            path="mylistings"
            component={requireAuth(MyListingsContainer)}
          />
          <Route
            path="mylistings/:listingId"
            component={requireAuth(ListingTOCContainer)}
          />
          <Route
            path="mylistings/:listingId/listing-details"
            component={requireAuth(ListingDetailsPage)}
          />
          <Route
            path="mylistings/:listingId/rules"
            component={requireAuth(ListingRulesPage)}
          />
          <Route
            path="mylistings/:listingId/price"
            component={requireAuth(EditPriceContainer)}
          />
          <Route
            path="mylistings/:listingId/fsv"
            component={requireAuth(ListingFsvPage)}
          />
          <Route
            path="your-space/:spaceId/availability"
            component={requireAuth(AddASpaceOpHoursContainer)}
          />
          <Route
            path="your-space(/:spaceId)/address"
            component={requireAuth(AddASpaceAddressContainer)}
          />
          <Route
            path="your-space/:spaceId/cancellation-policy"
            component={requireAuth(AddASpaceCancellationContainer)}
          />
          <Route
            path="your-space/:spaceId/cleaning-policy"
            component={requireAuth(CleaningContainer)}
          />
          <Route
            path="your-space(/:spaceId)"
            component={requireAuth(TransactableHOC(AddASpaceTocContainer))}
          />
        </Route>
      </Router>
    </Provider>
  );
};
export default RootMain;
